import { displayName } from '../../domain/names';
import { isAdmin, isEditor } from '../../domain/permissions';
import {
  DEFAULT_GOVERNANCE, DEFAULT_TREE_SETTINGS,
  type AccessCode, type AdminNomination, type AdminVote, type AssistantConfig, type AuditEvent, type ChangeRequest, type CommandResult, type DuplicateSuggestion, type FamilyEvent, type FamilyMembership, type Governance, type Invitation,
  type PersonRemovalProposal, type PersonWithNames, type Relationship, type Role, type Story, type TemporaryAccessSession, type TreeSettings, type UUID, type WelcomeMessage,
} from '../../domain/types';
import { consensusOutcome, tallyVotes } from '../../domain/consensus';
import { sameWelcome, tidyWelcome } from '../../domain/welcome';
import { daysFromNowISO, nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { AdminNominationView, AdminRepository, AdminSummary, FailedJob, MergePreview, NominationTally, PersonRemovalView, UpdatePersonCommand } from '../types';
import { actorOf, denied, guarded, invalid, notFound, okResult } from './actor';
import { MockDb } from './MockDb';
import { MockMediaRepository } from './MockMediaRepository';
import { MockPersonRepository } from './MockPersonRepository';

function randomCode(prefix: string): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let s = '';
  for (let i = 0; i < 4; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `${prefix.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8) || 'EDRY'}-${s}`;
}

export class MockAdminRepository implements AdminRepository {
  constructor(private db: MockDb, private people: MockPersonRepository, private media: MockMediaRepository) {}

  async getTreeSettings(): Promise<TreeSettings> {
    await this.db.read();
    const s = this.db.getSession();
    const familyId = s?.familyId ?? this.db.t.families[0]?.id ?? '';
    // Defaults underneath, so a row saved before a setting existed still has a value for it.
    const row = this.db.t.tree_settings.find((x) => x.familyId === familyId);
    return { familyId, ...DEFAULT_TREE_SETTINGS, updatedAt: '', ...(row ?? {}) };
  }

  async updateTreeSettings(patch: Partial<Omit<TreeSettings, 'familyId' | 'updatedAt'>>): Promise<CommandResult<TreeSettings>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      if (!isAdmin(actor.session.role)) return denied();
      const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
      const current = await this.getTreeSettings();
      const next: TreeSettings = { ...current, ...patch, familyId: actor.session.familyId, updatedAt: nowISO() };
      next.nodeScaleDefault = clamp(next.nodeScaleDefault, 0.6, 1.6);
      next.nodeScaleMax = clamp(next.nodeScaleMax, next.nodeScaleDefault, 2);
      next.nodeFadeDays = clamp(next.nodeFadeDays, 1, 365);
      next.badgeMinPx = clamp(next.badgeMinPx, 8, 40);
      next.badgeMaxPx = clamp(next.badgeMaxPx, next.badgeMinPx, 60);
      next.badgeFadeDays = clamp(next.badgeFadeDays, 1, 365);
      next.globeZoomMin = clamp(next.globeZoomMin, 0.4, 1);
      next.globeZoomMax = clamp(next.globeZoomMax, 1, 3);
      this.db.t.tree_settings = this.db.t.tree_settings.filter((x) => x.familyId !== next.familyId).concat(next);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: next.familyId, action: 'update', description: 'Changed how the tree grows and settles' });
      await this.db.commit(['tree_settings', 'audit_events']);
      return okResult(next);
    });
  }

  private requireEditor(): CommandResult<never> | null {
    const a = actorOf(this.db);
    return isEditor(a.session.role) ? null : denied('error.editorOnly');
  }

  private requireAdmin(): CommandResult<never> | null {
    const a = actorOf(this.db);
    return isAdmin(a.session.role) ? null : denied('error.adminOnly');
  }

  // ---- change requests --------------------------------------------------

  async listChangeRequests(status?: ChangeRequest['status']): Promise<ChangeRequest[]> {
    await this.db.read();
    return this.db.t.change_requests.filter((c) => !status || c.status === status).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async approveChangeRequest(id: UUID, idempotencyKey: string): Promise<CommandResult<ChangeRequest>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const db = this.db;
      const actor = actorOf(db);
      const cr = db.t.change_requests.find((c) => c.id === id);
      if (!cr) return notFound();
      if (cr.status === 'approved' || db.idem(idempotencyKey)) return okResult(cr, { noop: true });
      const now = nowISO();
      const p = cr.payload as Record<string, unknown>;
      switch (cr.kind) {
        case 'relationship_add': {
          const ids = (p.relationshipIds as UUID[] | undefined) ?? (p.relationshipId ? [p.relationshipId as UUID] : []);
          for (const rid of ids) {
            const rel = db.t.relationships.find((r) => r.id === rid);
            if (rel) {
              rel.status = 'approved';
              rel.version += 1;
              rel.updatedAt = now;
              rel.updatedBy = actor.session.accountId;
            }
          }
          if (!ids.length && p.fromPersonId && p.toPersonId) {
            db.t.relationships.push({ id: uuid(), familyId: cr.familyId, type: p.type as Relationship['type'], fromPersonId: p.fromPersonId as UUID, toPersonId: p.toPersonId as UUID, subtype: (p.subtype as Relationship['subtype']) ?? 'unspecified', status: 'approved', locked: false, version: 1, createdAt: now, updatedAt: now, updatedBy: actor.session.accountId });
          }
          break;
        }
        case 'relationship_remove': {
          db.t.relationships = db.t.relationships.filter((r) => r.id !== p.relationshipId);
          break;
        }
        case 'relationship_change': {
          const rel = db.t.relationships.find((r) => r.id === p.relationshipId);
          if (rel) Object.assign(rel, p.patch as Partial<Relationship>, { version: rel.version + 1, updatedAt: now, updatedBy: actor.session.accountId });
          break;
        }
        case 'person_field': {
          const cmd: UpdatePersonCommand = { personId: p.personId as UUID, expectedVersion: -1, patch: (p.patch as UpdatePersonCommand['patch']) ?? (p.field ? { [p.field as string]: p.to } : {}), idempotencyKey: `${idempotencyKey}:apply` };
          const r = await this.people.applyUpdate(cmd, true);
          if (!r.ok) return r as unknown as CommandResult<ChangeRequest>;
          break;
        }
        case 'person_add': {
          const person = db.t.persons.find((x) => x.id === p.personId);
          if (person) person.reviewStatus = 'approved';
          break;
        }
        case 'claim': {
          const link = db.t.person_account_links.find((l) => l.accountId === p.accountId && l.personId === p.personId);
          for (const other of db.t.person_account_links) if (other.personId === p.personId && other.accountId !== p.accountId && other.status === 'active') other.status = 'revoked';
          if (link) link.status = 'active';
          else db.t.person_account_links.push({ id: uuid(), accountId: p.accountId as UUID, personId: p.personId as UUID, kind: 'self', status: 'active', createdAt: now });
          break;
        }
        case 'conflict_escalation': {
          const cmd: UpdatePersonCommand = { personId: p.personId as UUID, expectedVersion: -1, patch: p.patch as UpdatePersonCommand['patch'], idempotencyKey: `${idempotencyKey}:apply` };
          const r = await this.people.applyUpdate(cmd, true);
          if (!r.ok) return r as unknown as CommandResult<ChangeRequest>;
          break;
        }
        case 'merge':
          break;
      }
      cr.status = 'approved';
      cr.reviewerAccountId = actor.session.accountId;
      cr.resolution = 'Approved';
      cr.updatedAt = now;
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'change_request', entityId: cr.id, action: 'approve', description: `Approved: ${cr.summary}`, approvalStatus: 'approved' });
      db.notify({ accountId: cr.requesterAccountId, kind: 'approval', title: `Approved: ${cr.summary}`, titleHe: `אושר: ${cr.summaryHe ?? cr.summary}`, link: cr.targetEntityType === 'person' ? `/people/${cr.targetEntityId}` : '/tree' });
      db.rememberIdem(idempotencyKey, 'change_request', cr.id);
      await db.commit(['change_requests', 'relationships', 'persons', 'person_account_links', 'audit_events', 'notifications', 'idempotency'], [cr.targetEntityId ?? cr.id]);
      return okResult(cr);
    });
  }

  async rejectChangeRequest(id: UUID, resolution: string, idempotencyKey: string): Promise<CommandResult<ChangeRequest>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const db = this.db;
      const actor = actorOf(db);
      const cr = db.t.change_requests.find((c) => c.id === id);
      if (!cr) return notFound();
      if (cr.status === 'rejected' || db.idem(idempotencyKey)) return okResult(cr, { noop: true });
      const p = cr.payload as Record<string, unknown>;
      const now = nowISO();
      if (cr.kind === 'relationship_add') {
        const ids = (p.relationshipIds as UUID[] | undefined) ?? (p.relationshipId ? [p.relationshipId as UUID] : []);
        for (const rid of ids) {
          const rel = db.t.relationships.find((r) => r.id === rid);
          if (rel) rel.status = 'rejected';
        }
      }
      if (cr.kind === 'relationship_remove') {
        const rel = db.t.relationships.find((r) => r.id === p.relationshipId);
        if (rel && rel.status === 'disputed') rel.status = 'approved';
      }
      if (cr.kind === 'person_add') {
        const person = db.t.persons.find((x) => x.id === p.personId);
        if (person) {
          person.reviewStatus = 'rejected';
          person.archived = true;
        }
      }
      if (cr.kind === 'claim') {
        const link = db.t.person_account_links.find((l) => l.accountId === p.accountId && l.personId === p.personId && l.status === 'pending');
        if (link) link.status = 'revoked';
      }
      cr.status = 'rejected';
      cr.reviewerAccountId = actor.session.accountId;
      cr.resolution = resolution;
      cr.updatedAt = now;
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'change_request', entityId: cr.id, action: 'reject', description: `Rejected: ${cr.summary} — ${resolution}` });
      db.notify({ accountId: cr.requesterAccountId, kind: 'approval', title: `Not applied: ${cr.summary}`, titleHe: `לא בוצע: ${cr.summaryHe ?? cr.summary}`, body: resolution, bodyHe: resolution });
      db.rememberIdem(idempotencyKey, 'change_request', cr.id);
      await db.commit(['change_requests', 'relationships', 'persons', 'person_account_links', 'audit_events', 'notifications', 'idempotency'], [cr.targetEntityId ?? cr.id]);
      return okResult(cr);
    });
  }

  async askQuestion(id: UUID, question: string): Promise<CommandResult<ChangeRequest>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const cr = this.db.t.change_requests.find((c) => c.id === id);
      if (!cr) return notFound();
      cr.status = 'question';
      cr.reviewerAccountId = actor.session.accountId;
      cr.reviewerQuestion = question;
      cr.updatedAt = nowISO();
      this.db.notify({ accountId: cr.requesterAccountId, kind: 'approval', title: `${this.db.accountName(actor.session.accountId)} asked about: ${cr.summary}`, titleHe: `${this.db.accountName(actor.session.accountId)} שאל/ה על: ${cr.summaryHe ?? cr.summary}`, body: question, bodyHe: question, link: '/me' });
      await this.db.commit(['change_requests', 'notifications']);
      return okResult(cr);
    });
  }

  async answerQuestion(id: UUID, answer: string): Promise<CommandResult<ChangeRequest>> {
    return guarded(this.db, async () => {
      const actor = actorOf(this.db);
      const cr = this.db.t.change_requests.find((c) => c.id === id);
      if (!cr) return notFound();
      if (cr.requesterAccountId !== actor.session.accountId && !isEditor(actor.session.role)) return denied();
      cr.reason = `${cr.reason ?? ''}\n— ${answer}`.trim();
      cr.status = 'pending';
      cr.updatedAt = nowISO();
      if (cr.reviewerAccountId) this.db.notify({ accountId: cr.reviewerAccountId, kind: 'review', title: `Answer received: ${cr.summary}`, titleHe: `התקבלה תשובה: ${cr.summaryHe ?? cr.summary}`, link: '/manage/review' });
      await this.db.commit(['change_requests', 'notifications']);
      return okResult(cr);
    });
  }

  // ---- duplicates & merges -----------------------------------------------

  async listDuplicates(): Promise<DuplicateSuggestion[]> {
    await this.db.read();
    return this.db.t.duplicate_suggestions.filter((d) => d.status === 'open');
  }

  async dismissDuplicate(id: UUID): Promise<CommandResult<DuplicateSuggestion>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const d = this.db.t.duplicate_suggestions.find((x) => x.id === id);
      if (!d) return notFound();
      d.status = 'dismissed';
      await this.db.commit(['duplicate_suggestions']);
      return okResult(d);
    });
  }

  async previewMerge(keepId: UUID, removeId: UUID): Promise<MergePreview> {
    await this.db.read();
    const keep = this.db.personWithNames(keepId)!;
    const remove = this.db.personWithNames(removeId)!;
    const rels = this.db.t.relationships.filter((r) => r.fromPersonId === removeId || r.toPersonId === removeId);
    const storyIds = new Set([
      ...this.db.t.story_people.filter((sp) => sp.personId === removeId).map((sp) => sp.storyId),
      ...this.db.t.stories.filter((s) => s.primaryStorytellerPersonId === removeId).map((s) => s.id),
    ]);
    const warnings: string[] = [];
    if (keep.status !== remove.status) warnings.push('status');
    if (keep.lockedFields.length || remove.lockedFields.length) warnings.push('locked');
    const keepLink = this.db.t.person_account_links.filter((l) => l.personId === keepId && l.status === 'active').length;
    const removeLink = this.db.t.person_account_links.filter((l) => l.personId === removeId && l.status === 'active').length;
    if (keepLink && removeLink) warnings.push('twoAccounts');
    return {
      keep, remove, relationshipsMoved: rels,
      storiesMoved: this.db.t.stories.filter((s) => storyIds.has(s.id)),
      contributionsMoved: this.db.t.story_contributions.filter((c) => c.speakerPersonId === removeId),
      accountsMoved: removeLink,
      grantsMoved: this.db.t.profile_management_grants.filter((g) => g.managedPersonId === removeId && g.status === 'active').length,
      warnings,
    };
  }

  async mergePeople(keepId: UUID, removeId: UUID, idempotencyKey: string): Promise<CommandResult<PersonWithNames>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const db = this.db;
      const actor = actorOf(db);
      if (db.idem(idempotencyKey)) return okResult(db.personWithNames(keepId)!, { noop: true });
      const keep = db.t.persons.find((p) => p.id === keepId);
      const remove = db.t.persons.find((p) => p.id === removeId);
      if (!keep || !remove) return notFound();
      if (keepId === removeId) return invalid('merge.error.same');
      const now = nowISO();
      // Relationships: re-point, drop exact duplicates and self-links
      for (const r of db.t.relationships) {
        if (r.fromPersonId === removeId) r.fromPersonId = keepId;
        if (r.toPersonId === removeId) r.toPersonId = keepId;
        if (r.type === 'partner' && r.toPersonId < r.fromPersonId) [r.fromPersonId, r.toPersonId] = [r.toPersonId, r.fromPersonId];
      }
      const seen = new Set<string>();
      db.t.relationships = db.t.relationships.filter((r) => {
        if (r.fromPersonId === r.toPersonId) return false;
        const k = `${r.type}:${r.fromPersonId}:${r.toPersonId}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      for (const sp of db.t.story_people) if (sp.personId === removeId) sp.personId = keepId;
      for (const s of db.t.stories) if (s.primaryStorytellerPersonId === removeId) s.primaryStorytellerPersonId = keepId;
      for (const c of db.t.story_contributions) if (c.speakerPersonId === removeId) c.speakerPersonId = keepId;
      for (const m of db.t.media_assets) if (m.speakerPersonId === removeId) m.speakerPersonId = keepId;
      for (const l of db.t.person_account_links) if (l.personId === removeId) { l.personId = keepId; if (db.t.person_account_links.some((o) => o !== l && o.personId === keepId && o.status === 'active')) l.status = 'pending'; }
      for (const g of db.t.profile_management_grants) if (g.managedPersonId === removeId) g.managedPersonId = keepId;
      // Names from the removed record are kept as non-preferred alternates
      for (const n of db.t.person_names.filter((x) => x.personId === removeId)) {
        const dup = db.t.person_names.some((k) => k.personId === keepId && k.language === n.language && k.given === n.given && k.family === n.family);
        if (!dup) db.t.person_names.push({ ...n, id: uuid(), personId: keepId, preferred: false });
      }
      remove.mergedIntoId = keepId;
      remove.archived = true;
      remove.updatedAt = now;
      keep.version += 1;
      keep.updatedAt = now;
      keep.updatedBy = actor.session.accountId;
      for (const d of db.t.duplicate_suggestions) if ((d.personAId === keepId && d.personBId === removeId) || (d.personAId === removeId && d.personBId === keepId)) d.status = 'merged';
      for (const cr of db.t.change_requests) if (cr.kind === 'person_add' && cr.targetEntityId === removeId && cr.status === 'pending') { cr.status = 'approved'; cr.resolution = 'Merged into existing person'; cr.reviewerAccountId = actor.session.accountId; cr.updatedAt = now; }
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'person', entityId: keepId, action: 'merge', before: { mergedFrom: removeId }, description: `Merged ${displayName(db.personWithNames(removeId)!, 'en')} into ${displayName(db.personWithNames(keepId)!, 'en')}`, entityVersion: keep.version });
      db.rememberIdem(idempotencyKey, 'person', keepId);
      await db.commit(['persons', 'person_names', 'relationships', 'story_people', 'stories', 'story_contributions', 'media_assets', 'person_account_links', 'profile_management_grants', 'duplicate_suggestions', 'change_requests', 'audit_events', 'idempotency'], [keepId, removeId]);
      return okResult(db.personWithNames(keepId)!);
    });
  }

  // ---- access ---------------------------------------------------------------

  async listAccessCodes(): Promise<AccessCode[]> {
    await this.db.read();
    return [...this.db.t.access_codes].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async createAccessCode(label: string, days: number): Promise<CommandResult<AccessCode>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const code: AccessCode = { id: uuid(), familyId: actor.session.familyId, code: randomCode('EDRY'), label: label.trim() || 'Family access code', expiresAt: daysFromNowISO(days), revoked: false, maxUses: 50, uses: 0, createdAt: nowISO() };
      this.db.t.access_codes.push(code);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: code.id, action: 'create', description: `Family access code created (${code.label}, ${days} days)` });
      await this.db.commit(['access_codes', 'audit_events']);
      return okResult(code);
    });
  }

  async revokeAccessCode(id: UUID): Promise<CommandResult<AccessCode>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const code = this.db.t.access_codes.find((c) => c.id === id);
      if (!code) return notFound();
      code.revoked = true;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: code.id, action: 'revoke', description: `Family access code ${code.code} revoked` });
      await this.db.commit(['access_codes', 'audit_events']);
      return okResult(code);
    });
  }

  async listInvitations(): Promise<Invitation[]> {
    await this.db.read();
    return [...this.db.t.invitations].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  async createInvitation(personId: UUID, destination: string): Promise<CommandResult<Invitation>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      if (!destination.trim()) return invalid('access.invite.needDestination');
      const person = this.db.personWithNames(personId);
      if (!person) return notFound();
      const inv: Invitation = { id: uuid(), familyId: actor.session.familyId, token: randomCode('INV'), intendedPersonId: personId, destination: destination.trim(), expiresAt: daysFromNowISO(14), status: 'pending', createdByAccountId: actor.session.accountId, createdAt: nowISO() };
      this.db.t.invitations.push(inv);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: inv.id, action: 'create', description: `Invitation sent to ${inv.destination} for ${displayName(person, 'en')}` });
      await this.db.commit(['invitations', 'audit_events']);
      return okResult(inv);
    });
  }

  async revokeInvitation(id: UUID): Promise<CommandResult<Invitation>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const inv = this.db.t.invitations.find((i) => i.id === id);
      if (!inv) return notFound();
      inv.status = 'revoked';
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: inv.id, action: 'revoke', description: `Invitation to ${inv.destination} revoked` });
      await this.db.commit(['invitations', 'audit_events']);
      return okResult(inv);
    });
  }

  async listTemporarySessions(): Promise<TemporaryAccessSession[]> {
    await this.db.read();
    return [...this.db.t.temporary_access_sessions].sort((a, b) => b.issuedAt.localeCompare(a.issuedAt));
  }

  async issueTemporaryCode(personId: UUID, deviceLabel: string, days: number): Promise<CommandResult<TemporaryAccessSession>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const db = this.db;
      const actor = actorOf(db);
      const person = db.personWithNames(personId);
      if (!person) return notFound();
      // One temporary account per person; reuse if it exists so history stays attached to the same account.
      let account = db.t.accounts.find((a) => a.kind === 'temporary' && db.t.person_account_links.some((l) => l.accountId === a.id && l.personId === personId && l.status === 'active'));
      const now = nowISO();
      if (!account) {
        account = { id: uuid(), kind: 'temporary', displayName: displayName(person, 'en'), preferredLang: 'he', createdAt: now, hasRecoveryIdentity: false };
        db.t.accounts.push(account);
        db.t.family_memberships.push({ id: uuid(), familyId: actor.session.familyId, accountId: account.id, role: 'member', status: 'active', createdAt: now });
        db.t.person_account_links.push({ id: uuid(), accountId: account.id, personId, kind: 'self', status: 'active', createdAt: now });
      }
      const given = person.names.find((n) => n.language === 'en')?.given ?? 'FAMILY';
      const s: TemporaryAccessSession = { id: uuid(), familyId: actor.session.familyId, personId, accountId: account.id, code: randomCode(given), deviceLabel: deviceLabel.trim() || 'Device', issuedByAccountId: actor.session.accountId, issuedAt: now, expiresAt: daysFromNowISO(days), revoked: false };
      db.t.temporary_access_sessions.push(s);
      db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: s.id, action: 'create', description: `Temporary device access issued for ${displayName(person, 'en')} (${s.deviceLabel}, ${days} days)` });
      await db.commit(['accounts', 'family_memberships', 'person_account_links', 'temporary_access_sessions', 'audit_events']);
      return okResult(s);
    });
  }

  async revokeTemporarySession(id: UUID): Promise<CommandResult<TemporaryAccessSession>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const s = this.db.t.temporary_access_sessions.find((x) => x.id === id);
      if (!s) return notFound();
      s.revoked = true;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'access', entityId: s.id, action: 'revoke', description: `Temporary device access revoked (${s.deviceLabel})` });
      await this.db.commit(['temporary_access_sessions', 'audit_events']);
      return okResult(s);
    });
  }

  async setRole(accountId: UUID, role: Role): Promise<CommandResult<FamilyMembership>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const m = this.db.membership(accountId);
      if (!m) return notFound();
      // Who the administrators are changes by agreement (nominateAdmin, stepDownAsAdmin) and never by
      // one administrator's say-so. This used to allow it — a select in Admin controls could appoint or
      // demote one in a tap, and the email list then quietly put a demoted administrator back.
      if (role === 'admin' || m.role === 'admin') return invalid('roles.error.adminByAgreement');
      if (m.role === role) return okResult(m, { noop: true });
      const before = m.role;
      m.role = role;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'account', entityId: accountId, action: 'role_change', before: { role: before }, after: { role }, description: `${this.db.accountName(accountId)}: ${before} → ${role}` });
      this.db.notify({ accountId, kind: 'approval', title: `Your role is now: ${role}`, titleHe: `התפקיד שלך עכשיו: ${role}` });
      await this.db.commit(['family_memberships', 'audit_events', 'notifications']);
      return okResult(m);
    });
  }

  async listLockedRecords(): Promise<{ people: PersonWithNames[]; relationships: Relationship[]; stories: Story[]; events: FamilyEvent[] }> {
    await this.db.read();
    return {
      // Either size of lock: some details, or the whole circle (`Person.locked`).
      people: this.db.t.persons.filter((p) => (p.lockedFields.length || p.locked) && !p.archived && !p.mergedIntoId).map((p) => this.db.personWithNames(p.id)!),
      relationships: this.db.t.relationships.filter((r) => r.locked),
      stories: this.db.t.stories.filter((s) => s.locked),
      events: this.db.t.events.filter((e) => e.locked),
    };
  }

  async listFailedJobs(): Promise<FailedJob[]> {
    await this.db.read();
    const out: FailedJob[] = [];
    for (const up of this.db.t.upload_sessions) {
      if (up.state === 'action_needed') out.push({ id: up.id, kind: 'upload', entityId: up.mediaAssetId, label: this.mediaLabel(up.mediaAssetId), error: up.lastError ?? 'Upload failed', attempts: up.attempts, at: up.updatedAt });
    }
    for (const tr of this.db.t.transcripts) {
      if (tr.status === 'failed') {
        const c = this.db.t.story_contributions.find((x) => x.id === tr.contributionId);
        out.push({ id: tr.id, kind: 'transcription', entityId: tr.contributionId, label: c ? this.mediaLabel(c.mediaAssetId ?? '') : 'Transcript', error: tr.error ?? 'Transcription failed', attempts: 1, at: tr.updatedAt });
      }
    }
    for (const m of this.db.t.media_assets) {
      if (m.backupState === 'failed') out.push({ id: `backup:${m.id}`, kind: 'backup', entityId: m.id, label: this.mediaLabel(m.id), error: 'Independent backup copy failed', attempts: 1, at: m.updatedAt });
    }
    return out.sort((a, b) => b.at.localeCompare(a.at));
  }

  private mediaLabel(mediaId: UUID): string {
    const c = this.db.t.story_contributions.find((x) => x.mediaAssetId === mediaId);
    const s = c ? this.db.t.stories.find((x) => x.id === c.storyId) : undefined;
    const speaker = c ? this.db.personWithNames(c.speakerPersonId) : undefined;
    return `${s?.title ?? 'Recording'}${speaker ? ` — ${displayName(speaker, 'en')}` : ''}`;
  }

  async retryJob(job: FailedJob): Promise<CommandResult<{ queued: true }>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      if (job.kind === 'upload') await this.media.retryUpload(job.entityId);
      if (job.kind === 'transcription') await this.media.retryTranscription(job.entityId);
      if (job.kind === 'backup') await this.media.retryBackup(job.entityId);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'media', entityId: job.entityId, action: 'update', description: `Retried ${job.kind}: ${job.label}` });
      await this.db.commit(['audit_events']);
      return okResult({ queued: true as const });
    });
  }

  async listAudit(limit = 200, entityId?: UUID): Promise<AuditEvent[]> {
    await this.db.read();
    return this.db.t.audit_events.filter((a) => !entityId || a.entityId === entityId).sort((a, b) => b.timestamp.localeCompare(a.timestamp)).slice(0, limit);
  }

  async restoreFromAudit(auditEventId: UUID, idempotencyKey: string): Promise<CommandResult<{ restored: true }>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireEditor();
      if (deniedResult) return deniedResult;
      const db = this.db;
      const actor = actorOf(db);
      if (db.idem(idempotencyKey)) return okResult({ restored: true as const }, { noop: true });
      const ev = db.t.audit_events.find((a) => a.id === auditEventId);
      if (!ev) return notFound();
      if (ev.entityType === 'person' && ev.action === 'update' && ev.before) {
        const r = await this.people.applyUpdate({ personId: ev.entityId, expectedVersion: -1, patch: ev.before as UpdatePersonCommand['patch'], idempotencyKey: `${idempotencyKey}:apply` }, true);
        if (!r.ok) return r as unknown as CommandResult<{ restored: true }>;
      } else if (ev.entityType === 'person' && ev.action === 'archive') {
        const p = db.t.persons.find((x) => x.id === ev.entityId);
        if (p) p.archived = false;
      } else if (ev.entityType === 'relationship' && ev.action === 'archive' && ev.before?.relationship) {
        const rel = ev.before.relationship as Relationship;
        if (!db.t.relationships.some((r) => r.id === rel.id)) db.t.relationships.push({ ...rel, status: 'approved', version: rel.version + 1, updatedAt: nowISO(), updatedBy: actor.session.accountId });
      } else if (ev.entityType === 'contribution' && ev.action === 'archive') {
        const c = db.t.story_contributions.find((x) => x.id === ev.entityId);
        if (c) c.status = 'published';
      } else if (ev.entityType === 'story' && ev.action === 'update' && ev.before) {
        const s = db.t.stories.find((x) => x.id === ev.entityId);
        if (s) Object.assign(s, ev.before, { version: s.version + 1, updatedAt: nowISO(), updatedBy: actor.session.accountId });
      } else {
        return invalid('audit.error.notRestorable');
      }
      db.audit({ actorAccountId: actor.session.accountId, entityType: ev.entityType, entityId: ev.entityId, action: 'restore', description: `Restored previous values (compensating change for ${ev.description})` });
      db.rememberIdem(idempotencyKey, ev.entityType, ev.entityId);
      await db.commit(['persons', 'person_names', 'relationships', 'stories', 'story_contributions', 'audit_events', 'idempotency'], [ev.entityId]);
      return okResult({ restored: true as const });
    });
  }

  // -------------------------------------------------------------------------
  // Who runs the family
  //
  // Administrators are an email list, and the list only changes by agreement.
  // A nomination passes on a majority of the administrators who have actually
  // been around lately, so a board that has drifted away cannot freeze the
  // family; and, if even that majority never turns up, an unopposed nomination
  // carries itself after `nominationTimeoutDays`. One objection closes that
  // second route, so anything contested still needs the real majority.
  // -------------------------------------------------------------------------

  /**
   * The family's assistant settings — readable by everybody, because every
   * member's browser is the thing that has to make the call.
   */
  async getAssistantConfig(): Promise<AssistantConfig | undefined> {
    await this.db.read();
    return this.db.t.families[0]?.assistant;
  }

  async updateAssistantConfig(patch: Partial<AssistantConfig>): Promise<CommandResult<AssistantConfig>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const fam = this.db.t.families[0];
      if (!fam) return notFound();
      const current: AssistantConfig = fam.assistant ?? {
        provider: 'local', model: '', apiKey: '', baseUrl: '',
        listenWith: 'browser', listenKey: '', listenUrl: '',
        speakWith: 'browser', speakKey: '', speakUrl: '', voiceName: '',
      };
      const next: AssistantConfig = {
        ...current,
        ...patch,
        updatedAt: nowISO(),
        updatedBy: actor.session.accountId,
      };
      fam.assistant = next;
      /*
       * The key itself is never written to the decision log. Administrators can
       * read that log, and a secret that ends up in an append-only history is a
       * secret that cannot be rotated away — so what is recorded is that the
       * setting changed and who changed it, which is all anybody needs.
       */
      this.db.audit({
        actorAccountId: actor.session.accountId,
        entityType: 'family',
        entityId: fam.id,
        action: 'update',
        description: `Changed who answers the Help button (${next.provider}; listens: ${next.listenWith}, speaks: ${next.speakWith})`,
      });
      await this.db.commit(['families', 'audit_events']);
      return okResult(next);
    });
  }

  async updateWelcome(text: { en: string; he: string }): Promise<CommandResult<WelcomeMessage>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const fam = this.db.t.families[0];
      if (!fam) return notFound();
      const next = { en: tidyWelcome(text.en), he: tidyWelcome(text.he) };
      if (!next.en && !next.he) return invalid('greeting.error.empty');
      // The same words as are already up: nothing is saved, and nobody is sent back to the board.
      if (fam.welcome && sameWelcome(fam.welcome, next)) return okResult(fam.welcome, { noop: true });
      const at = nowISO();
      fam.welcome = { ...next, updatedAt: at, updatedBy: actor.session.accountId };
      // Whoever wrote it has read it.
      const mine = this.db.membership(actor.session.accountId);
      if (mine) mine.welcomeSeenAt = at;
      this.db.audit({
        actorAccountId: actor.session.accountId,
        actorRole: 'admin',
        entityType: 'family',
        entityId: fam.id,
        action: 'update',
        description: 'Changed the welcome message — everyone will be shown it again',
      });
      await this.db.commit(['families', 'family_memberships', 'audit_events']);
      return okResult(fam.welcome);
    });
  }

  async getGovernance(): Promise<Governance> {
    await this.db.read();
    return this.db.governance();
  }

  async updateGovernance(patch: Partial<Omit<Governance, 'adminEmails'>>): Promise<CommandResult<Governance>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const fam = this.db.t.families[0];
      if (!fam) return notFound();
      const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(v)));
      const current = this.db.governance();
      const next: Governance = {
        ...current,
        ...patch,
        adminEmails: current.adminEmails,
      };
      next.activeAdminDays = clamp(next.activeAdminDays, 7, 365);
      next.nominationTimeoutDays = clamp(next.nominationTimeoutDays, 1, 180);
      next.memberApprovalsRequired = clamp(next.memberApprovalsRequired, 1, 10);
      fam.governance = next;
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: fam.id, action: 'update', description: 'Changed how the family agrees on administrators and new members' });
      await this.db.commit(['families', 'audit_events']);
      return okResult(next);
    });
  }

  async listAdmins(): Promise<AdminSummary[]> {
    await this.db.read();
    this.db.syncAdminEmails();
    const active = new Set(this.db.activeAdminAccountIds());
    const gov = this.db.governance();
    const byEmail = new Map<string, AdminSummary>();
    const lastSeen = (accountId: UUID): string | undefined => {
      let best: string | undefined;
      for (const ev of this.db.t.audit_events) {
        if (ev.actorAccountId === accountId && (!best || ev.timestamp > best)) best = ev.timestamp;
      }
      return best;
    };
    // Every address on the list, whether or not anyone has signed in with it yet.
    for (const raw of gov.adminEmails) {
      const email = raw.trim().toLowerCase();
      if (!email) continue;
      const account = this.db.t.accounts.find((a) => a.email?.trim().toLowerCase() === email);
      byEmail.set(email, {
        email,
        accountId: account?.id,
        displayName: account?.displayName ?? email,
        active: account ? active.has(account.id) : false,
        lastSeenAt: account ? lastSeen(account.id) : undefined,
        awaitingSignIn: !account,
      });
    }
    // Administrators whose role was set directly rather than by email (older families).
    for (const id of this.db.adminAccountIds()) {
      const account = this.db.t.accounts.find((a) => a.id === id);
      const email = account?.email?.trim().toLowerCase() ?? `account:${id}`;
      if (byEmail.has(email)) continue;
      byEmail.set(email, {
        email: account?.email ?? '',
        accountId: id,
        displayName: account?.displayName ?? '—',
        active: active.has(id),
        lastSeenAt: lastSeen(id),
        awaitingSignIn: false,
      });
    }
    return [...byEmail.values()].sort((a, b) => Number(b.active) - Number(a.active) || a.displayName.localeCompare(b.displayName));
  }

  /**
   * Where a proposal the administrators must agree on stands right now — never
   * stored, always recomputed. The rule itself lives in domain/consensus.ts, so
   * nominations and removals cannot drift apart.
   */
  private tallyOf(p: { votes: AdminVote[]; createdAt: string; status: string }, myAccountId: UUID | null): NominationTally {
    return tallyVotes({
      votes: p.votes,
      activeAdminIds: this.db.activeAdminAccountIds(),
      createdAt: p.createdAt,
      timeoutDays: this.db.governance().nominationTimeoutDays,
      open: p.status === 'open',
      myAccountId,
    });
  }

  /** Carry out whatever the votes and the clock now say. Returns true when anything changed. */
  private settleNominations(): boolean {
    let changed = false;
    for (const n of this.db.t.admin_nominations) {
      if (n.status !== 'open') continue;
      const outcome = consensusOutcome(this.tallyOf(n, null));
      if (outcome === 'carry' || outcome === 'timeout') {
        this.grantAdmin(n, outcome === 'carry' ? 'majority' : 'timeout');
        changed = true;
      } else if (outcome === 'decline') {
        n.status = 'declined';
        n.decidedBy = 'objection';
        n.decidedAt = nowISO();
        this.db.audit({ actorAccountId: null, actorRole: 'admin', entityType: 'family', entityId: n.id, action: 'reject', description: `Administrator nomination for ${n.email} was declined by the administrators` });
        changed = true;
      }
    }
    return changed;
  }

  // -------------------------------------------------------------------------
  // Removing a circle
  //
  // Taking someone out of the family tree is never one administrator's call: it
  // goes to the same agreement as adding an administrator. When it carries the
  // circle is archived — the record, its stories and the reason all survive, and
  // History & restore can bring it back.
  // -------------------------------------------------------------------------

  /** Apply whatever the votes and the clock say about the open removals. */
  private settleRemovals(): boolean {
    let changed = false;
    for (const r of this.db.t.person_removals) {
      if (r.status !== 'open') continue;
      const outcome = consensusOutcome(this.tallyOf(r, null));
      if (outcome === 'carry' || outcome === 'timeout') {
        this.archiveCircle(r, outcome === 'carry' ? 'majority' : 'timeout');
        changed = true;
      } else if (outcome === 'decline') {
        r.status = 'declined';
        r.decidedBy = 'objection';
        r.decidedAt = nowISO();
        this.db.audit({ actorAccountId: null, actorRole: 'admin', entityType: 'person', entityId: r.personId, action: 'reject', description: 'The administrators decided not to remove this circle' });
        changed = true;
      }
    }
    return changed;
  }

  private archiveCircle(r: PersonRemovalProposal, how: 'majority' | 'timeout'): void {
    const person = this.db.t.persons.find((p) => p.id === r.personId);
    r.status = 'approved';
    r.decidedBy = how;
    r.decidedAt = nowISO();
    if (!person || person.archived) return;
    const named = this.db.personWithNames(r.personId);
    person.archived = true;
    person.version += 1;
    person.updatedAt = nowISO();
    // Nobody is left pointing at a circle that is no longer in the tree.
    for (const l of this.db.t.person_account_links) if (l.personId === r.personId && l.status === 'active') l.status = 'revoked';
    for (const g of this.db.t.profile_management_grants) if (g.managedPersonId === r.personId && g.status === 'active') g.status = 'revoked';
    this.db.audit({
      actorAccountId: r.proposedByAccountId,
      actorRole: 'admin',
      entityType: 'person',
      entityId: r.personId,
      action: 'archive',
      reason: r.reason,
      description: how === 'majority'
        ? `Removed ${named ? displayName(named, 'en') : 'a circle'} from the tree — a majority of the administrators agreed`
        : `Removed ${named ? displayName(named, 'en') : 'a circle'} from the tree — nobody objected within ${this.db.governance().nominationTimeoutDays} days`,
      entityVersion: person.version,
    });
  }

  async listPersonRemovals(): Promise<PersonRemovalView[]> {
    await this.db.read();
    if (this.settleRemovals()) {
      await this.db.commit(['person_removals', 'persons', 'person_account_links', 'profile_management_grants', 'audit_events']);
    }
    const me = this.db.getSession()?.accountId ?? null;
    return [...this.db.t.person_removals]
      .sort((a, b) => Number(b.status === 'open') - Number(a.status === 'open') || b.createdAt.localeCompare(a.createdAt))
      .map((proposal) => ({ proposal, tally: this.tallyOf(proposal, me), person: this.db.personWithNames(proposal.personId) }));
  }

  async proposePersonRemoval(personId: UUID, reason?: string): Promise<CommandResult<PersonRemovalProposal>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const person = this.db.t.persons.find((p) => p.id === personId);
      if (!person || person.archived) return notFound();
      if (actor.session.personId === personId) return invalid('removal.error.self');
      if (this.db.t.person_removals.some((r) => r.status === 'open' && r.personId === personId)) return invalid('removal.error.openAlready');
      const now = nowISO();
      const proposal: PersonRemovalProposal = {
        id: uuid(),
        familyId: actor.session.familyId,
        personId,
        proposedByAccountId: actor.session.accountId,
        reason: reason?.trim() || undefined,
        // Proposing it is approving it — nobody votes for their own proposal twice.
        votes: [{ accountId: actor.session.accountId, vote: 'approve', reason: reason?.trim() || undefined, at: now }],
        status: 'open',
        createdAt: now,
      };
      this.db.t.person_removals.push(proposal);
      const named = this.db.personWithNames(personId);
      const who = named ? displayName(named, 'en') : 'a circle';
      this.db.audit({ actorAccountId: actor.session.accountId, actorRole: 'admin', entityType: 'person', entityId: personId, action: 'reject', reason: proposal.reason, description: `Proposed removing ${who} from the tree` });
      for (const id of this.db.adminAccountIds()) {
        if (id === actor.session.accountId) continue;
        this.db.notify({ accountId: id, kind: 'approval', title: `${this.db.accountName(actor.session.accountId)} proposed removing a circle`, titleHe: `${this.db.accountName(actor.session.accountId, 'he')} הציע/ה להסיר עיגול`, body: who, bodyHe: who, link: '/manage/people', actorAccountId: actor.session.accountId });
      }
      this.settleRemovals();
      await this.db.commit(['person_removals', 'persons', 'person_account_links', 'profile_management_grants', 'audit_events', 'notifications'], [personId]);
      return okResult(proposal);
    });
  }

  async voteOnPersonRemoval(id: UUID, vote: 'approve' | 'object', reason?: string): Promise<CommandResult<PersonRemovalProposal>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const r = this.db.t.person_removals.find((x) => x.id === id);
      if (!r) return notFound();
      if (r.status !== 'open') return invalid('admins.error.closed');
      const entry: AdminVote = { accountId: actor.session.accountId, vote, reason: reason?.trim() || undefined, at: nowISO() };
      r.votes = [...r.votes.filter((v) => v.accountId !== actor.session.accountId), entry];
      const named = this.db.personWithNames(r.personId);
      const who = named ? displayName(named, 'en') : 'a circle';
      this.db.audit({ actorAccountId: actor.session.accountId, actorRole: 'admin', entityType: 'person', entityId: r.personId, action: vote === 'approve' ? 'approve' : 'reject', reason: entry.reason, description: `${vote === 'approve' ? 'Agreed to remove' : 'Objected to removing'} ${who}` });
      this.settleRemovals();
      await this.db.commit(['person_removals', 'persons', 'person_account_links', 'profile_management_grants', 'audit_events'], [r.personId]);
      return okResult(r);
    });
  }

  async withdrawPersonRemoval(id: UUID): Promise<CommandResult<PersonRemovalProposal>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const r = this.db.t.person_removals.find((x) => x.id === id);
      if (!r) return notFound();
      if (r.status !== 'open') return invalid('admins.error.closed');
      if (r.proposedByAccountId !== actor.session.accountId) return denied();
      r.status = 'withdrawn';
      r.decidedBy = 'withdrawn';
      r.decidedAt = nowISO();
      this.db.audit({ actorAccountId: actor.session.accountId, actorRole: 'admin', entityType: 'person', entityId: r.personId, action: 'restore', description: 'Withdrew the proposal to remove this circle' });
      await this.db.commit(['person_removals', 'audit_events'], [r.personId]);
      return okResult(r);
    });
  }

  /** Put the address on the list and lift anyone already signed in with it. */
  private grantAdmin(n: AdminNomination, how: 'majority' | 'timeout'): void {
    const fam = this.db.t.families[0];
    if (!fam) return;
    const gov = this.db.governance();
    const email = n.email.trim().toLowerCase();
    if (!gov.adminEmails.some((e) => e.trim().toLowerCase() === email)) {
      fam.governance = { ...gov, adminEmails: [...gov.adminEmails, email] };
    }
    this.db.syncAdminEmails();
    n.status = 'approved';
    n.decidedBy = how;
    n.decidedAt = nowISO();
    const account = this.db.t.accounts.find((a) => a.email?.trim().toLowerCase() === email);
    if (account) n.accountId = account.id;
    this.db.audit({
      actorAccountId: null,
      actorRole: 'admin',
      entityType: 'family',
      entityId: n.id,
      action: 'role_change',
      after: { admin: email },
      description: how === 'majority'
        ? `${n.email} became an administrator — a majority of the administrators approved`
        : `${n.email} became an administrator — nobody objected within ${this.db.governance().nominationTimeoutDays} days`,
    });
    if (account) {
      this.db.notify({ accountId: account.id, kind: 'approval', title: 'You are now an administrator of the family', titleHe: 'את/ה עכשיו מנהל/ת של המשפחה' });
    }
  }

  async listAdminNominations(): Promise<AdminNominationView[]> {
    await this.db.read();
    // Timeouts are worked out on read: nothing here needs a server to tick.
    if (this.settleNominations()) await this.db.commit(['admin_nominations', 'families', 'family_memberships', 'audit_events', 'notifications']);
    const me = this.db.getSession()?.accountId ?? null;
    return [...this.db.t.admin_nominations]
      .sort((a, b) => Number(b.status === 'open') - Number(a.status === 'open') || b.createdAt.localeCompare(a.createdAt))
      .map((n) => ({ nomination: n, tally: this.tallyOf(n, me) }));
  }

  async nominateAdmin(email: string, reason?: string): Promise<CommandResult<AdminNomination>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const clean = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) return invalid('admins.error.email');
      if (this.db.governance().adminEmails.some((e) => e.trim().toLowerCase() === clean)) return invalid('admins.error.already');
      if (this.db.t.admin_nominations.some((n) => n.status === 'open' && n.email === clean)) return invalid('admins.error.openAlready');
      const now = nowISO();
      const account = this.db.t.accounts.find((a) => a.email?.trim().toLowerCase() === clean);
      const nomination: AdminNomination = {
        id: uuid(),
        familyId: actor.session.familyId,
        email: clean,
        accountId: account?.id,
        nominatedByAccountId: actor.session.accountId,
        reason: reason?.trim() || undefined,
        // Raising it is approving it — nobody has to vote for their own suggestion twice.
        votes: [{ accountId: actor.session.accountId, vote: 'approve', reason: reason?.trim() || undefined, at: now }],
        status: 'open',
        createdAt: now,
      };
      this.db.t.admin_nominations.push(nomination);
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: nomination.id, action: 'create', reason: nomination.reason, description: `Suggested ${clean} as an administrator` });
      for (const id of this.db.adminAccountIds()) {
        if (id === actor.session.accountId) continue;
        this.db.notify({ accountId: id, kind: 'approval', title: `${this.db.accountName(actor.session.accountId)} suggested a new administrator`, titleHe: `${this.db.accountName(actor.session.accountId, 'he')} הציע/ה מנהל/ת חדש/ה`, body: clean, bodyHe: clean, link: '/manage/admins', actorAccountId: actor.session.accountId });
      }
      this.settleNominations();
      await this.db.commit(['admin_nominations', 'families', 'family_memberships', 'audit_events', 'notifications']);
      return okResult(nomination);
    });
  }

  async voteOnNomination(id: UUID, vote: 'approve' | 'object', reason?: string): Promise<CommandResult<AdminNomination>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const n = this.db.t.admin_nominations.find((x) => x.id === id);
      if (!n) return notFound();
      if (n.status !== 'open') return invalid('admins.error.closed');
      const entry: AdminVote = { accountId: actor.session.accountId, vote, reason: reason?.trim() || undefined, at: nowISO() };
      n.votes = [...n.votes.filter((v) => v.accountId !== actor.session.accountId), entry];
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: n.id, action: vote === 'approve' ? 'approve' : 'reject', reason: entry.reason, description: `${vote === 'approve' ? 'Approved' : 'Objected to'} the administrator nomination for ${n.email}` });
      this.settleNominations();
      await this.db.commit(['admin_nominations', 'families', 'family_memberships', 'audit_events', 'notifications']);
      return okResult(n);
    });
  }

  async withdrawNomination(id: UUID): Promise<CommandResult<AdminNomination>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const n = this.db.t.admin_nominations.find((x) => x.id === id);
      if (!n) return notFound();
      if (n.status !== 'open') return invalid('admins.error.closed');
      if (n.nominatedByAccountId !== actor.session.accountId) return denied();
      n.status = 'withdrawn';
      n.decidedBy = 'withdrawn';
      n.decidedAt = nowISO();
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'family', entityId: n.id, action: 'reject', description: `Withdrew the administrator nomination for ${n.email}` });
      await this.db.commit(['admin_nominations', 'audit_events']);
      return okResult(n);
    });
  }

  async stepDownAsAdmin(): Promise<CommandResult<Governance>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const fam = this.db.t.families[0];
      if (!fam) return notFound();
      if (this.db.adminAccountIds().length <= 1) return invalid('roles.error.lastAdmin');
      const email = this.db.t.accounts.find((a) => a.id === actor.session.accountId)?.email?.trim().toLowerCase();
      const gov = this.db.governance();
      fam.governance = { ...gov, adminEmails: gov.adminEmails.filter((e) => e.trim().toLowerCase() !== email) };
      const m = this.db.membership(actor.session.accountId);
      if (m) m.role = 'editor';
      this.db.setAdminMode(false);
      this.db.audit({ actorAccountId: actor.session.accountId, actorRole: 'admin', entityType: 'account', entityId: actor.session.accountId, action: 'role_change', before: { role: 'admin' }, after: { role: 'editor' }, description: `${this.db.accountName(actor.session.accountId)} stepped down as an administrator` });
      await this.db.commit(['families', 'family_memberships', 'audit_events']);
      return okResult(fam.governance);
    });
  }

  // -------------------------------------------------------------------------
  // What the administrators have decided
  // -------------------------------------------------------------------------

  async listAdminDecisions(limit = 200): Promise<AuditEvent[]> {
    await this.db.read();
    return [...this.db.t.audit_events]
      .filter((e) => e.actorRole === 'admin')
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
      .slice(0, limit);
  }

  async removePortrait(portraitId: UUID, reason?: string): Promise<CommandResult<{ removed: true }>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const portrait = this.db.t.person_portraits.find((p) => p.id === portraitId);
      if (!portrait) return notFound();
      if (portrait.removed) return okResult({ removed: true as const }, { noop: true });
      portrait.removed = { byAccountId: actor.session.accountId, at: nowISO(), reason: reason?.trim() || undefined };
      const person = this.db.personWithNames(portrait.personId);
      // If it was the circle's photo, fall back to the newest one still standing.
      if (portrait.current) {
        portrait.current = false;
        const next = this.db.t.person_portraits
          .filter((p) => p.personId === portrait.personId && !p.removed)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
        if (next) next.current = true;
        const row = this.db.t.persons.find((p) => p.id === portrait.personId);
        if (row) {
          row.portraitUrl = next ? `portrait:${next.id}` : undefined;
          row.updatedAt = nowISO();
          row.updatedBy = actor.session.accountId;
        }
      }
      this.db.audit({
        actorAccountId: actor.session.accountId,
        actorRole: 'admin',
        entityType: 'media',
        entityId: portrait.id,
        action: 'remove',
        reason: portrait.removed.reason,
        media: { kind: 'portrait', id: portrait.id, src: `portrait:${portrait.id}` },
        description: `Removed a photo from ${person ? displayName(person, 'en') : 'a circle'}`,
      });
      await this.db.commit(['person_portraits', 'persons', 'audit_events'], [portrait.personId]);
      return okResult({ removed: true as const });
    });
  }

  async removeMoment(photoId: UUID, reason?: string): Promise<CommandResult<{ removed: true }>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const photo = this.db.t.family_photos.find((p) => p.id === photoId);
      if (!photo) return notFound();
      if (photo.removed) return okResult({ removed: true as const }, { noop: true });
      photo.removed = { byAccountId: actor.session.accountId, at: nowISO(), reason: reason?.trim() || undefined };
      this.db.audit({
        actorAccountId: actor.session.accountId,
        actorRole: 'admin',
        entityType: 'media',
        entityId: photo.id,
        action: 'remove',
        reason: photo.removed.reason,
        media: photo.storageKey ? { kind: 'moment', id: photo.id, src: `moment:${photo.id}` } : undefined,
        description: photo.title || photo.caption
          ? `Removed “${photo.title || photo.caption}”, shared by ${this.db.accountName(photo.uploadedByAccountId)}`
          : `Removed a picture shared by ${this.db.accountName(photo.uploadedByAccountId)}`,
      });
      await this.db.commit(['family_photos', 'audit_events'], [photo.id]);
      return okResult({ removed: true as const });
    });
  }

  async restoreMedia(kind: 'portrait' | 'moment', id: UUID): Promise<CommandResult<{ restored: true }>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      if (kind === 'portrait') {
        const portrait = this.db.t.person_portraits.find((p) => p.id === id);
        if (!portrait) return notFound();
        portrait.removed = undefined;
        this.db.audit({ actorAccountId: actor.session.accountId, actorRole: 'admin', entityType: 'media', entityId: id, action: 'restore', media: { kind, id, src: `portrait:${id}` }, description: 'Put a removed photo back' });
        await this.db.commit(['person_portraits', 'audit_events'], [portrait.personId]);
      } else {
        const photo = this.db.t.family_photos.find((p) => p.id === id);
        if (!photo) return notFound();
        photo.removed = undefined;
        this.db.audit({ actorAccountId: actor.session.accountId, actorRole: 'admin', entityType: 'media', entityId: id, action: 'restore', media: photo.storageKey ? { kind, id, src: `moment:${id}` } : undefined, description: 'Put a removed moment back' });
        await this.db.commit(['family_photos', 'audit_events'], [id]);
      }
      return okResult({ restored: true as const });
    });
  }

  async disconnectAccountFromPerson(accountId: UUID, personId: UUID): Promise<CommandResult<{ disconnected: true }>> {
    return guarded(this.db, async () => {
      const deniedResult = this.requireAdmin();
      if (deniedResult) return deniedResult;
      const actor = actorOf(this.db);
      const link = this.db.t.person_account_links.find((l) => l.accountId === accountId && l.personId === personId && l.status === 'active');
      if (!link) return notFound();
      link.status = 'revoked';
      this.db.audit({ actorAccountId: actor.session.accountId, entityType: 'account', entityId: accountId, action: 'revoke', description: 'Account disconnected from person (person and stories kept)' });
      await this.db.commit(['person_account_links', 'audit_events']);
      return okResult({ disconnected: true as const });
    });
  }
}
